import React, { useRef, useState } from "react";
import {
    Alert,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    CssBaseline,
    Grid,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { axiosClient } from "../AxiosClient";
import { exportViabilityToExcel } from "./exportViabilityExcel";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
}));

const ButtonPrincipal = styled(Button)({
    flexGrow: 0,
    borderRadius: "20px !important",
    gap: "10px !important",
    backgroundColor: "#161e55 !important",
    color: "#ffff !important",
    "&:hover": {
        backgroundColor: "#A8C8AF !important",
    },
});

const tableHeaders = [
    "Número documento",
    "Apellido",
    "Consulta",
    "Status",
    "Valor aprobado",
];

export default function ViabilityStudyByDocument() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");
    const [exportingExcel, setExportingExcel] = useState(false);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            setResults([]);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!file) {
            setSnackbarMessage("Selecciona un archivo CSV para continuar.");
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("file", file);

        axiosClient
            .post("/v1/importer/viability-study-by-document", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((response) => {
                setLoading(false);
                const data = Array.isArray(response.data) ? response.data : [];
                setResults(data);
                setSnackbarMessage(
                    data.length > 0
                        ? `Se procesaron ${data.length} registro(s) correctamente.`
                        : "No se encontraron registros en el archivo."
                );
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            })
            .catch((error) => {
                setLoading(false);
                setSnackbarMessage(
                    error.response?.data?.message ||
                        "Error al procesar el archivo. Verifica el formato e intenta de nuevo."
                );
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbarOpen(false);
    };

    const handleBack = () => {
        navigate("/homeAdmin/clientes");
    };

    const handleDownloadExcel = async () => {
        if (results.length === 0) return;
        setExportingExcel(true);
        try {
            await exportViabilityToExcel(results);
            setSnackbarMessage("Excel descargado correctamente.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarMessage(
                err.message || "Error al generar el archivo Excel."
            );
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setExportingExcel(false);
        }
    };

    return (
        <React.Fragment>
            <CssBaseline />
            <Toolbar />
            <Box sx={{ width: "100%", margin: "18px 14px" }}>
                <Stack spacing={2}>
                    <Item>.</Item>
                    <Item></Item>
                    <Grid container>
                        <Grid item xs={12}>
                            <Box
                                mt={2}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <h1>Generar estudio de viabilidad por documento</h1>
                                <ButtonPrincipal onClick={handleBack}>
                                    Volver a Clientes
                                </ButtonPrincipal>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Item sx={{ p: 2, textAlign: "left" }}>
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1.5,
                                        }}
                                    >
                                        <Box sx={{ display: "block" }}>
                                            <Box
                                                component="label"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: "text.secondary",
                                                    display: "block",
                                                }}
                                            >
                                                Archivo CSV
                                            </Box>
                                            {file && (
                                                <Box
                                                    sx={{
                                                        fontSize: "0.875rem",
                                                        color: "text.secondary",
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    {file.name}
                                                </Box>
                                            )}
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                alignItems: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <input
                                                accept=".csv"
                                                type="file"
                                                hidden
                                                ref={fileInputRef}
                                                onChange={handleFileChange}
                                            />
                                            <ButtonPrincipal
                                                type="button"
                                                onClick={() =>
                                                    fileInputRef.current?.click()
                                                }
                                            >
                                                Seleccionar archivo
                                            </ButtonPrincipal>
                                            <ButtonPrincipal type="submit">
                                                Enviar
                                            </ButtonPrincipal>
                                        </Box>
                                    </Box>
                                </form>
                            </Item>
                        </Grid>
                        <Grid item xs={12}>
                            {results.length > 0 && (
                                <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
                                    <ButtonPrincipal
                                        onClick={handleDownloadExcel}
                                        disabled={exportingExcel}
                                    >
                                        {exportingExcel ? "Generando..." : "Descargar Excel"}
                                    </ButtonPrincipal>
                                </Box>
                            )}
                            <TableContainer component={Paper}>
                                <Table
                                    sx={{ minWidth: 650 }}
                                    aria-label="resultados viabilidad"
                                >
                                    <TableHead>
                                        <TableRow>
                                            {tableHeaders.map((header) => (
                                                <TableCell
                                                    key={header}
                                                    align="center"
                                                >
                                                    {header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {results.length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={
                                                        tableHeaders.length
                                                    }
                                                    align="center"
                                                    sx={{ py: 4 }}
                                                >
                                                    {loading
                                                        ? "Procesando..."
                                                        : "Sube un archivo y haz clic en Enviar para ver los resultados."}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            results.map((row, index) => (
                                                <TableRow
                                                    key={
                                                        row.numeroDocumento +
                                                        "-" +
                                                        index
                                                    }
                                                    sx={{
                                                        "&:last-child td, &:last-child th":
                                                            { border: 0 },
                                                        backgroundColor:
                                                            String(row.status || "").toLowerCase() === "rechazado"
                                                                ? "rgba(211, 47, 47, 0.12)"
                                                                : String(row.status || "").toLowerCase() === "aprobado"
                                                                ? "rgba(46, 125, 50, 0.12)"
                                                                : "inherit",
                                                    }}
                                                >
                                                    <TableCell align="center">
                                                        {row.numeroDocumento}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {row.apellido}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {row.consulta}
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color:
                                                                String(row.status || "").toLowerCase() === "rechazado"
                                                                    ? "error.main"
                                                                    : String(row.status || "").toLowerCase() === "aprobado"
                                                                    ? "success.main"
                                                                    : "text.primary",
                                                        }}
                                                    >
                                                        {row.status}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {row.valorAprobado !=
                                                        null
                                                            ? Number(
                                                                  row.valorAprobado
                                                              ).toLocaleString()
                                                            : "-"}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Stack>
            </Box>
            <Backdrop
                sx={{
                    background: "rgba(255,255,255,0.1)",
                    zIndex: 5,
                    width: "50%",
                    margin: "auto auto auto 30vw",
                }}
                open={loading || exportingExcel}
            >
                <CircularProgress color="success" />
            </Backdrop>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
}
